import React from 'react';
import InsightArticle from '../../components/InsightArticle';
import { useLanguage } from '../../context/LanguageContext';

const GymLoseLeads = () => {
  const { lang } = useLanguage();

  const content = {
    en: {
      title: "5 Reasons Barcelona Gyms Lose Instagram Leads",
      description: "Analysis of common conversion leaks in gym marketing and how to fix them.",
      category: "Gym Growth",
      date: "Jan 20, 2026",
      keyTakeaway: "Most gyms don't have a content problem; they have a process problem that breaks down between the DM and the booking.",
      body: (
        <>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            A gym doesn't need a "marketing problem." It needs someone to look honestly at what happens between a follower getting curious and a follower walking in the door. Usually, it breaks down at one of these <span className="text-orange-500 font-semibold">five critical points</span>.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">1. Slow DM response time</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Someone messages "hey, how much is a trial class?" at 8pm. They get an answer at 11am the next day. By then they've either messaged three other gyms or forgotten about it entirely. <span className="text-orange-500 font-semibold">Response speed isn't a nice-to-have</span> — it's the single biggest predictor of whether a DM turns into a booking. Every hour of delay is measurable lost revenue, even if nobody's tracking it.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">2. No clear next step</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            "DM us for more info!" is <span className="text-orange-500 font-semibold">not a call to action, it's a wall</span>. It puts the entire burden of figuring out price, schedule, and process on the lead. Most people won't do that work. They'll just leave. A working system tells people exactly what happens next: message this number, get this info instantly, book this specific slot.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">3. Price hidden until the very end</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Hiding pricing doesn't create more leads — it creates more unqualified DMs and more people who drop off the moment they finally hear the number. <span className="text-orange-500 font-semibold">Being upfront about a trial price</span> filters in people who are actually ready and filters out the ones who were never going to convert anyway.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">4. Content that impresses but doesn't inform</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Cinematic gym reels look great and do very little for lead generation if they never answer "what would my first week actually look like." Aspirational content builds brand. <span className="text-orange-500 font-semibold">Informational content builds bookings</span>. Gyms need both, but most only post the first kind.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">5. No system to catch the "almost" leads</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Someone asks about pricing, goes quiet, never books. Without a <span className="text-orange-500 font-semibold">simple follow-up process</span> — one message, a few days later, no pressure — that lead is just gone. Most gyms have zero process for this. It's the easiest fix on this whole list, and the one most consistently ignored.
          </p>

          <h3 className="text-2xl font-bold text-white mt-12 mb-4 uppercase tracking-tighter">The pattern</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg italic">
            None of these are content problems. They're <span className="text-orange-500 font-semibold">process problems</span> that show up through Instagram. Fixing them doesn't require a bigger budget — it requires treating the DM inbox and the booking flow as seriously as the workout programming.
          </p>

          <div className="mt-12 p-8 bg-zinc-900 border border-orange-500/30 rounded-[2rem]">
            <p className="text-xl text-white font-bold mb-4">
              Every one of these five leaks has the same fix.
            </p>
            <p className="text-gray-400 mb-6 leading-relaxed">
              My <span className="text-white font-bold">AI Lead Assistant</span> answers Instagram DMs the moment they land, qualifies the prospect, and follows up automatically so nobody just "goes quiet." Feed it with a <span className="text-white font-bold">Content Day</span> and it has real conversation starters to work with, too.
            </p>
            <p className="text-orange-500 font-black uppercase tracking-widest text-sm">
              Test it yourself: send "SYSTEM" to my Instagram.
            </p>
          </div>
        </>
      )
    },
    es: {
      title: "5 Razones por las que los Gimnasios de Barcelona Pierden Leads en Instagram",
      description: "Análisis de las fugas comunes de conversión en el marketing de gimnasios y cómo solucionarlas.",
      category: "Crecimiento de Gimnasios",
      date: "20 Ene 2026",
      keyTakeaway: "La mayoría de los gimnasios no tienen un problema de contenido; tienen un problema de proceso que se rompe entre el mensaje directo y la reserva.",
      body: (
        <>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Un gimnasio no necesita un "problema de marketing". Necesita que alguien analice honestamente qué sucede entre que un seguidor siente curiosidad y que ese seguidor entra por la puerta. Normalmente, el proceso se rompe en uno de estos <span className="text-orange-500 font-semibold">cinco puntos críticos</span>.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">1. Tiempo de respuesta lento en DMs</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Alguien escribe "¿cuánto cuesta una clase de prueba?" a las 8 de la tarde. Recibe respuesta a las 11 de la mañana del día siguiente. Para entonces, o bien ha escrito a otros tres gimnasios o se ha olvidado por completo. <span className="text-orange-500 font-semibold">La velocidad de respuesta no es un extra</span>; es el mayor predictor de si un DM se convierte en una reserva. Cada hora de retraso es una pérdida de ingresos medible.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">2. Sin un siguiente paso claro</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            "¡Escríbenos para más info!" <span className="text-orange-500 font-semibold">no es una llamada a la acción, es un muro</span>. Carga toda la responsabilidad de averiguar el precio y el horario sobre el lead. La mayoría de la gente no hará ese trabajo. Un sistema que funciona dice exactamente qué pasa después: envía un mensaje, recibe info al instante, reserva tu hueco.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">3. Precio oculto hasta el final</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Ocultar los precios no genera más leads; genera más DMs no cualificados y abandonos. <span className="text-orange-500 font-semibold">Ser transparente con el precio de la prueba</span> filtra a las personas que realmente están listas y descarta a las que nunca iban a convertir de todos modos.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">4. Contenido que impresiona pero no informa</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Los reels cinematográficos se ven geniales, pero hacen poco por la generación de leads si no responden a "¿cómo sería mi primera semana?". El contenido aspiracional construye marca. El <span className="text-orange-500 font-semibold">contenido informativo construye reservas</span>.
          </p>

          <h3 className="text-2xl font-bold text-white mt-10 mb-4">5. Sin sistema para captar los leads "casi" ganados</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg">
            Alguien pregunta, se queda callado, nunca reserva. Sin un <span className="text-orange-500 font-semibold">proceso de seguimiento sencillo</span>, ese lead simplemente desaparece. Es la solución más fácil de toda esta lista, y la que se ignora con más frecuencia.
          </p>

          <h3 className="text-2xl font-bold text-white mt-12 mb-4 uppercase tracking-tighter">El patrón</h3>
          <p className="mb-6 text-gray-300 leading-relaxed text-lg italic">
            Ninguno de estos es un problema de contenido. Son <span className="text-orange-500 font-semibold">problemas de proceso</span> que se manifiestan en Instagram. Solucionarlos no requiere más presupuesto; requiere tratar la bandeja de entrada y el flujo de reservas con la misma seriedad que la programación de los entrenamientos.
          </p>

          <div className="mt-12 p-8 bg-zinc-900 border border-orange-500/30 rounded-[2rem]">
            <p className="text-xl text-white font-bold mb-4">
              Cada una de estas cinco fugas tiene la misma solución.
            </p>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Mi <span className="text-white font-bold">Asistente de Leads con IA</span> responde a los DMs de Instagram en cuanto llegan, cualifica al prospecto y hace el seguimiento automático para que nadie se quede "en silencio". Aliméntalo con un <span className="text-white font-bold">Día de Contenido</span> y tendrá iniciadores de conversación reales con los que trabajar.
            </p>
            <p className="text-orange-500 font-black uppercase tracking-widest text-sm">
              Pruébalo tú mismo: envía "SISTEMA" a mi Instagram.
            </p>
          </div>
        </>
      )
    }
  };

  const t = content[lang] || content.en;

  return (
    <InsightArticle
      title={t.title}
      description={t.description}
      category={t.category}
      date={t.date}
      readTime="6"
      keyTakeaway={t.keyTakeaway}
    >
      {t.body}
    </InsightArticle>
  );
};

export default GymLoseLeads;